/**
 * MobileFormProgressBar — Phase 9.1: Mobile-Optimized Form Renderer
 *
 * A floating / inline progress bar for mobile form filling.
 * Shows current section, overall completion %, and section dots/pills.
 *
 * Usage:
 *   <MobileFormProgressBar
 *     sections={['Inspection','Safety','Sign-off']}
 *     currentIndex={1}
 *     completedIndices={[0]}
 *     onSectionTap={(idx) => goToSection(idx)}
 *   />
 *
 * @phase 9.1 - Mobile-Optimized Form Renderer
 */

'use client';

import React from 'react';
import { CheckCircle2, Circle, AlertCircle } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface MobileFormProgressBarProps {
  /** Section labels / names */
  sections: string[];
  /** Zero-based index of the currently visible section */
  currentIndex: number;
  /** Indices of sections that are completed */
  completedIndices?: number[];
  /** Indices of sections that have errors / blockers */
  errorIndices?: number[];
  /** Called when user taps a section dot to jump */
  onSectionTap?: (index: number) => void;
  /** Optional className */
  className?: string;
  /** Variant style */
  variant?: 'dots' | 'pills' | 'bar';
  /** Accent color used for active state and progress */
  accentColor?: string;
  /** Optional header background color */
  backgroundColor?: string;
}

export function MobileFormProgressBar({
  sections,
  currentIndex,
  completedIndices = [],
  errorIndices = [],
  onSectionTap,
  className,
  variant = 'dots',
  accentColor = '#00affa',
  backgroundColor,
}: MobileFormProgressBarProps) {
  const total = sections.length;
  const completedCount = completedIndices.length;
  const pct = total > 0 ? Math.round((completedCount / total) * 100) : 0;
  const resolvedBackgroundColor = backgroundColor || accentColor;

  if (total === 0) return null;

  // ── Bar variant ────────────────────────────────────────────────
  if (variant === 'bar') {
    return (
      <div
        className={cn('px-4 py-2.5 border-b shadow-[inset_0_-1px_0_rgba(255,255,255,0.12)]', className)}
        style={{ backgroundColor: resolvedBackgroundColor, borderColor: 'rgba(255,255,255,0.18)' }}
      >
        <div className="mb-1 flex items-center justify-between text-xs text-white/90">
          <span className="max-w-[60%] truncate font-semibold text-white">
            {sections[currentIndex] || `Section ${currentIndex + 1}`}
          </span>
          <span className="font-medium text-white/95">{completedCount}/{total} · {pct}%</span>
        </div>
        <div className="h-1.5 overflow-hidden rounded-full bg-white/30">
          <div
            className="h-full rounded-full bg-white transition-all duration-300"
            style={{ width: `${pct}%` }}
          />
        </div>
      </div>
    );
  }

  // ── Pills variant ──────────────────────────────────────────────
  if (variant === 'pills') {
    return (
      <div
        className={cn('border-b px-3 py-2 shadow-[inset_0_-1px_0_rgba(255,255,255,0.12)]', className)}
        style={{ backgroundColor: resolvedBackgroundColor, borderColor: 'rgba(255,255,255,0.18)' }}
      >
        <div className="flex gap-1.5 overflow-x-auto scrollbar-hide pb-0.5">
          {sections.map((label, idx) => {
            const isActive = idx === currentIndex;
            const isCompleted = completedIndices.includes(idx);
            const hasError = errorIndices.includes(idx);
            return (
              <button
                key={idx}
                onClick={() => onSectionTap?.(idx)}
                className={cn(
                  'flex-shrink-0 px-3 py-1.5 rounded-full text-xs font-medium transition-all min-h-[32px]',
                  'active:scale-95 touch-manipulation',
                  isActive
                    ? 'bg-white text-sky-600 shadow-sm'
                    : isCompleted
                    ? 'bg-white/20 text-white'
                    : hasError
                    ? 'bg-red-500/20 text-white'
                    : 'bg-white/12 text-white/80'
                )}
              >
                {label}
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  // ── Dots variant (default) ─────────────────────────────────────
  return (
    <div
      className={cn('flex items-center justify-center gap-1.5 border-b px-4 py-2', className)}
      style={{ backgroundColor: resolvedBackgroundColor, borderColor: 'rgba(255,255,255,0.18)' }}
    >
      {/* Section label */}
      <span className="mr-2 max-w-[120px] truncate text-xs text-white/85">
        {sections[currentIndex]}
      </span>

      {/* Dots */}
      {sections.map((_, idx) => {
        const isActive = idx === currentIndex;
        const isCompleted = completedIndices.includes(idx);
        const hasError = errorIndices.includes(idx);
        return (
          <button
            key={idx}
            onClick={() => onSectionTap?.(idx)}
            className="p-0.5 touch-manipulation"
            aria-label={`Go to section ${idx + 1}: ${sections[idx]}`}
          >
            {hasError ? (
              <AlertCircle className={cn('h-3.5 w-3.5', isActive ? 'text-white' : 'text-white/80')} />
            ) : isCompleted ? (
              <CheckCircle2 className={cn('h-3.5 w-3.5', isActive ? 'text-white' : 'text-white/85')} />
            ) : (
              <Circle
                className={cn(
                  'h-3 w-3 transition-all',
                  isActive
                    ? 'fill-white text-white'
                    : 'text-white/45'
                )}
              />
            )}
          </button>
        );
      })}

      {/* Completion percentage */}
      <span className="ml-2 text-xs text-white/85">
        {pct}%
      </span>
    </div>
  );
}

export default MobileFormProgressBar;
